<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  sender: {
    type: Object,
    default: null,
  },
  inboxName: {
    type: String,
    default: '',
  },
  inboxIcon: {
    type: String,
    default: '',
  },
  // GrowerIQ: { fires, replied, emails } for this campaign. Chatwoot ships no
  // campaign metrics; these come from conversations.campaign_id, preloaded by
  // the API in a single grouped query.
  stats: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const senderName = computed(
  () => props.sender?.name || t('CAMPAIGN.LIVE_CHAT.CARD.CAMPAIGN_DETAILS.BOT')
);

const senderThumbnailSrc = computed(() => props.sender?.thumbnail);

const fires = computed(() => props.stats?.fires ?? 0);
const replied = computed(() => props.stats?.replied ?? 0);
const emails = computed(() => props.stats?.emails ?? 0);
const hasFired = computed(() => fires.value > 0);
// Converting = it won at least one email. These are the campaigns to leave alone.
const isConverting = computed(() => emails.value > 0);
</script>

<template>
  <span class="flex-shrink-0 text-sm text-n-slate-11 whitespace-nowrap">
    {{ t('CAMPAIGN.LIVE_CHAT.CARD.CAMPAIGN_DETAILS.SENT_BY') }}
  </span>
  <div class="flex items-center gap-1.5 flex-shrink-0">
    <Avatar
      :name="senderName"
      :src="senderThumbnailSrc"
      :size="16"
      rounded-full
    />
    <span class="text-sm font-medium text-n-slate-12">
      {{ senderName }}
    </span>
  </div>
  <span class="flex-shrink-0 text-sm text-n-slate-11 whitespace-nowrap">
    {{ t('CAMPAIGN.LIVE_CHAT.CARD.CAMPAIGN_DETAILS.FROM') }}
  </span>
  <div class="flex items-center gap-1.5 flex-shrink-0">
    <Icon :icon="inboxIcon" class="flex-shrink-0 text-n-slate-12 size-3" />
    <span class="text-sm font-medium text-n-slate-12">
      {{ inboxName }}
    </span>
  </div>
  <!-- GrowerIQ: performance at a glance. A converting campaign is one to leave
       alone; a campaign that has fired plenty and never converted is free to rewrite. -->
  <div
    v-if="hasFired"
    class="flex items-center gap-1.5 flex-shrink-0"
    :title="`${fires} fires, ${replied} replied, ${emails} email${emails === 1 ? '' : 's'} captured`"
  >
    <span class="text-n-slate-9">&middot;</span>
    <span class="text-sm text-n-slate-11 whitespace-nowrap">
      {{ fires }} fires
    </span>
    <span class="text-sm text-n-slate-11 whitespace-nowrap">
      &middot; {{ replied }} replied
    </span>
    <span
      class="text-sm font-medium whitespace-nowrap"
      :class="isConverting ? 'text-n-teal-11' : 'text-n-slate-11'"
    >
      &middot; {{ emails }} email{{ emails === 1 ? '' : 's' }}
    </span>
  </div>
  <span
    v-else
    class="text-sm text-n-slate-9 whitespace-nowrap flex-shrink-0"
    title="This campaign has never fired"
  >
    &middot; never fired
  </span>
</template>
